import { useState, type FormEvent } from "react";
import { api, ApiError } from "./api";
import type { Me } from "./session";

/**
 * Uncontrolled login form: values are read from the form elements on submit (FormData),
 * so Safari/iOS autofill, password managers and typed input all work even when the
 * browser fills a field without firing React's onChange.
 */
export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const lastUsername = localStorage.getItem("cl.lastUsername") ?? "";
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const username = String(data.get("username") ?? "").trim();
    const password = String(data.get("password") ?? ""); // never altered
    if (!username || !password) { setError("Enter your username and password."); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await api.login(username, password);
      localStorage.setItem("cl.lastUsername", username);
      onSignedIn(r.user);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      setError(status === 401 ? "Wrong username or password." : status === 429 ? "Too many attempts; wait a minute and try again." : `Couldn't sign in: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <form onSubmit={submit} className="card login-card" method="post" action="/api/auth/login" noValidate>
        <div className="brand big"><span className="logo" /> Centerline Cloud</div>
        <p className="muted">Watson Construction · sign in with your own login</p>
        <label htmlFor="username">Username</label>
        <input id="username" name="username" type="text" autoFocus={!lastUsername} defaultValue={lastUsername} autoComplete="username" autoCapitalize="off" autoCorrect="off" spellCheck={false} />
        <label htmlFor="password">Password</label>
        <input id="password" name="password" type="password" autoFocus={!!lastUsername} autoComplete="current-password" autoCapitalize="off" autoCorrect="off" spellCheck={false} />
        {error && <div className="error" role="alert">{error}</div>}
        <button type="submit" className="primary" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        <p className="muted small">No login yet or forgot your password? Ask the office admin.</p>
      </form>
    </div>
  );
}
